import csvText from "../../map/nama_kampung_mukim_searched.csv?raw";
import type { Language } from "../types";

type CsvLocationRow = {
  source_file: string;
  layer: string;
  dun: string;
  nama_kampung_original: string;
  nama_kampung: string;
  mukim_original: string;
  mukim: string;
  mukim_status: string;
  mukim_final: string;
  mukim_final_source: string;
  mukim_confidence: string;
  mukim_note: string;
};

export type LocationOption = {
  value: string;
  label: Record<Language, string>;
  description?: Record<Language, string>;
  mukim: string;
};

function parseCsvLine(line: string) {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];

    if (char === '"' && inQuotes && next === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      inQuotes = !inQuotes;
      continue;
    }

    if (char === "," && !inQuotes) {
      cells.push(current);
      current = "";
      continue;
    }

    current += char;
  }

  cells.push(current);
  return cells;
}

function parseCsv(text: string) {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .filter(Boolean);
  const [headerLine, ...dataLines] = lines;
  const headers = parseCsvLine(headerLine);

  return dataLines.map((line) => {
    const cells = parseCsvLine(line);
    return headers.reduce((row, header, index) => {
      row[header as keyof CsvLocationRow] = cells[index] ?? "";
      return row;
    }, {} as CsvLocationRow);
  });
}

const rawRows = parseCsv(csvText);

const locationRows = rawRows.filter(
  (row) =>
    row.nama_kampung &&
    row.nama_kampung !== "Contoh" &&
    row.mukim_final &&
    row.mukim_final_source !== "not_found",
);

const mukimAliases: Record<string, string> = {
  buit: "Buit",
  lok_kawi: "Lok Kawi",
  pasir_putih: "Pasir Putih",
  petagas: "Petagas",
  putatan: "Bandar Putatan",
};

const kampungToMukim = new Map(
  locationRows.map((row) => [row.nama_kampung, row.mukim_final]),
);

const mukimValues = Array.from(
  new Set(locationRows.map((row) => row.mukim_final)),
);

export const kampungOptions: LocationOption[] = locationRows.map((row) => ({
  value: row.nama_kampung,
  label: {
    ms: `${row.nama_kampung} · ${row.mukim_final}`,
    en: `${row.nama_kampung} · ${row.mukim_final}`,
  },
  description: {
    ms: row.mukim_final,
    en: row.mukim_final,
  },
  mukim: row.mukim_final,
}));

export const mukimOptions = mukimValues.map((value) => ({
  value,
  label: { ms: value, en: value },
}));

export function getMukimForKampung(kampung: string) {
  return kampungToMukim.get(kampung) ?? "";
}

export function normalizeMukimValue(value: string) {
  return mukimAliases[value] ?? value;
}

export function getMukimLabel(value: string) {
  const normalized = normalizeMukimValue(value);
  return mukimOptions.find((option) => option.value === normalized)?.label.ms ??
    normalized;
}
